import React, { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { SG_FETCH_ACTION_UPDATE } from 'constants/actions';

export default function UpdateDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const state = useSelector((s) => s.action_updates || {}, shallowEqual);
  console.log('Render Details', { id, state });
  // Only use state[id] as the source of truth
  const update = state?.[id] || {};
  console.log('Render Details', { id, update });
  console.log('Render Details', update);

  useEffect(() => {
    if (id) dispatch({ type: SG_FETCH_ACTION_UPDATE, payload: id });
  }, [dispatch, id]);

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
  const pctNum = typeof update.progress_percentage === 'number'
    ? Math.max(0, Math.min(100, update.progress_percentage))
    : null;

  const mgr = update.manager || {};
  const managerName = (mgr.first_name || mgr.last_name)
    ? `${mgr.first_name || ''} ${mgr.last_name || ''}`.trim()
    : '—';

  return (
    <Page>
      <TopBar>
        <Link to="../list">← Back to list</Link>
      </TopBar>

      <Header>
        <div>
          <Title>Update Details</Title>
          <Sub>ID: {update.id || '—'}</Sub>
        </div>
        <Badge>{update.update_type || 'update'}</Badge>
      </Header>

      {!update?.id ? (
        <Card>Loading update…</Card>
      ) : (
        <>
          {/* Developer Notes */}
          <Card style={{ borderColor: '#fbbf24', background: '#fffbeb' }}>
            <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 8 }}>🔍 Developer Notes — Update Analysis</div>
            <div style={{ color: '#78350f', fontSize: 13, lineHeight: '1.6' }}>
              <div style={{ marginBottom: 6 }}><strong>Progress change:</strong> {update.previous_status ?? '-'} → {update.new_status ?? '-'} ({pctNum !== null ? `${pctNum}%` : '-'})</div>
              {Math.abs(pctNum ?? 0) > 50 && <div style={{ color: '#d97706' }}>⚠️ Large progress jump ({pctNum}%) in single update — verify accuracy</div>}
              {(update.progress_percentage ?? 0) === 0 && update.summary && <div style={{ color: '#2563eb' }}>ℹ️ Update has summary but 0% progress — may be status/comment update only</div>}
              {update.new_status === update.previous_status && (update.progress_percentage ?? 0) === 0 && <div style={{ color: '#d97706' }}>⚠️ No status change and 0% progress — update may not have meaningful changes</div>}
              {(!update.summary || update.summary.trim().length < 10) && <div style={{ color: '#d97706' }}>⚠️ Very short or missing summary — low quality update</div>}
              {update.satisfaction_rating && update.satisfaction_rating < 3 && <div style={{ color: '#dc2626' }}>⚠️ Low satisfaction rating ({update.satisfaction_rating}) — manager may need support</div>}
              {update.confidence_level && update.confidence_level < 3 && <div style={{ color: '#d97706' }}>⚠️ Low confidence level ({update.confidence_level}) — action may be at risk</div>}
            </div>
            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: 'pointer', color: '#92400e' }}>📖 View validation guide</summary>
              <div style={{ marginTop: 8, padding: 12, background: '#fff', borderRadius: 6, fontSize: 12, fontFamily: 'monospace' }}>
                <div><strong>Progress Delta Logic:</strong></div>
                <div style={{ marginTop: 4 }}>old_progress + progress_delta = new_progress</div>
                <div style={{ marginTop: 4 }}>Example: 30% + 15% = 45%</div>
                <div style={{ marginTop: 8 }}><strong>Red Flags:</strong></div>
                <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
                  <li>progress_delta &gt; 50 in single update → suspiciously large</li>
                  <li>Multiple updates same created_at → possible duplicate</li>
                  <li>No updates for &gt;14 days on active action → stale</li>
                </ul>
              </div>
            </details>
          </Card>

          <Card>
            <Grid>
              <Item>
                <Label>Action Title</Label>
                <Value>{update.action_title || '—'}</Value>
              </Item>
              <Item>
                <Label>Organization</Label>
                <Value>
                  {update.organization?.name || '—'}
                  {update.organization?.id ? <Small> (ID: {update.organization.id})</Small> : null}
                </Value>
              </Item>

              <Item full>
                <Label>Summary</Label>
                <Value>{update.summary || '—'}</Value>
              </Item>

              <Item>
                <Label>Progress</Label>
                <Value>
                  {pctNum !== null ? `${pctNum}%` : '—'}
                  <Progress>
                    <ProgressFill style={{ width: pctNum !== null ? `${pctNum}%` : '0%' }} />
                  </Progress>
                </Value>
              </Item>

              <Item>
                <Label>Satisfaction Rating</Label>
                <Value>{update.satisfaction_rating ?? '—'}</Value>
              </Item>

              <Item>
                <Label>Confidence Level</Label>
                <Value>{update.confidence_level ?? '—'}</Value>
              </Item>

              <Item>
                <Label>Previous Status</Label>
                <Value>{update.previous_status ?? '—'}</Value>
              </Item>

              <Item>
                <Label>New Status</Label>
                <Value>{update.new_status ?? '—'}</Value>
              </Item>

              <Item>
                <Label>Manager</Label>
                <Value>
                  {managerName}
                  {mgr.email ? <Small> — {mgr.email}</Small> : null}
                </Value>
              </Item>

              <Item>
                <Label>Created</Label>
                <Value>{fmt(update.created_at)}</Value>
              </Item>
              <Item>
                <Label>Updated</Label>
                <Value>{fmt(update.updated_at)}</Value>
              </Item>
            </Grid>
          </Card>

          <Section>
            <details>
              <summary>Raw JSON</summary>
              <Code>{JSON.stringify(update, null, 2)}</Code>
            </details>
          </Section>
        </>
      )}
    </Page>
  );
}


/* styled */
const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Title = styled.h2`
  margin: 0 0 4px 0;
`;
const Sub = styled.div`
  color: #6b7280;
  font-size: 12px;
`;
const Badge = styled.span`
  color: #2563eb;
  border: 1px solid #2563eb;
  background: #eef2ff;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  text-transform: capitalize;
`;
const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 12px 16px;
`;
const Item = styled.div`
  grid-column: ${(p) => (p.full ? '1 / -1' : 'span 6')};
  min-width: 0;
`;
const Label = styled.div`
  font-size: 12px;
  color: #6b7280;
`;
const Value = styled.div`
  font-size: 14px;
  word-break: break-word;
`;
const Small = styled.span`
  color: #6b7280;
  font-size: 12px;
`;
const Section = styled(Card)`
  padding: 8px 12px;
`;
const Code = styled.pre`
  background: #0b1020;
  color: #e5e7eb;
  padding: 12px;
  border-radius: 6px;
  overflow: auto;
  font-size: 12px;
  margin: 8px 0 0 0;
`;
const Progress = styled.div`
  margin-top: 6px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
`;
const ProgressFill = styled.div`
  height: 100%;
  background: #2563eb;
`;